import React, { useRef, useLayoutEffect, useState } from "react";
import { Box } from "@mui/material";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { explanatoryNote, reportForm, workerGroupedData } from "./Report";
import { ReportLegend } from "./ReportLegend";
import { useThemeMode } from "Contexts/ThemeContext";

type TimeAxisChartProps = {
  startDate: string;
  endDate: string;
  workerGroupedData: workerGroupedData[];
  reportForm: reportForm;
  legend: explanatoryNote[];
  chartHeight: number;
  filteredLegend: explanatoryNote[];
  setFilteredLegend: React.Dispatch<React.SetStateAction<explanatoryNote[]>>;
};

export const ReportTimeAxisChart: React.FC<TimeAxisChartProps> = ({
  startDate,
  endDate,
  workerGroupedData,
  reportForm,
  legend,
  chartHeight,
  filteredLegend,
  setFilteredLegend,
}) => {
  const timeAxisChartRef = useRef<HTMLDivElement>(null);
  const { mode } = useThemeMode();

  useLayoutEffect(() => {
    const labelColor = am5.color(mode === "dark" ? 0xffffff : 0x4b0000);
    const ss = `${reportForm.startDate} 00:00:00`; // 開始日付の後に 00:00:00 を追加
    const ee = `${reportForm.endDate} 23:59:59`; // 終了日付の後に 23:59:59 を追加

    if (!timeAxisChartRef.current) {
      // 必要に応じてエラーハンドリングを行うか、return する
      return;
    }

    // 時間軸チャート作成
    const root = am5.Root.new(timeAxisChartRef.current!);
    root.setThemes([am5themes_Animated.new(root)]);
    root.dateFormatter.set("intlLocales", "ja-JP");

    const areaColors: { [key: string]: am5.Color } = {};

    // チャート作成
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        layout: root.horizontalLayout, // 横方向にレイアウトを変更
        panX: true,
        panY: true,
        wheelX: "panX",
        wheelY: "zoomX",
        paddingBottom: 40, // X軸タイトル用の余白
        paddingLeft: 60, // Y軸タイトル用の余白
      })
    );

    // Y軸 (名前)
    const yAxis = chart.yAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "yDisp",
        renderer: am5xy.AxisRendererY.new(root, {}),
      })
    );

    // X軸 (日時)
    const XAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        baseInterval: { timeUnit: "hour", count: 1 },
        renderer: am5xy.AxisRendererX.new(root, {
          opposite: false,
          inside: false,
          minGridDistance: 10,
        }),
        maxDeviation: 0.1, // 余白の許容値
        // 各単位ごとの表示フォーマットを指定
        dateFormats: {
          hour: "HH:mm",
          day: "MM/dd",
        },
        // 期間変更時の表示フォーマット（必要に応じて）
        periodChangeDateFormats: {
          hour: "HH:mm",
          day: "MM/dd",
        },
        min: new Date(ss).getTime(),
        max: new Date(ee).getTime(),
      })
    );

    // X軸のラベルを調整
    XAxis.get("renderer").labels.template.setAll({
      inside: false, // 軸の外側に配置
      paddingTop: 10, // チャートとの間隔を広げる
      fontSize: 12,
      fill: labelColor,
    });

    // ラベルテキストのアダプターを追加
    XAxis.get("renderer").labels.template.adapters.add(
      "text",
      (text, target) => {
        // 現在のズーム状態（0～1の割合）と軸全体の min/max を取得
        const startFraction = XAxis.get("start");
        const endFraction = XAxis.get("end");
        const axisMin = XAxis.get("min");
        const axisMax = XAxis.get("max");

        if (
          startFraction === undefined ||
          endFraction === undefined ||
          axisMin === undefined ||
          axisMax === undefined
        ) {
          return text;
        }

        // 可視範囲の開始/終了タイムスタンプを計算
        const visibleStart = axisMin + (axisMax - axisMin) * startFraction;
        const visibleEnd = axisMin + (axisMax - axisMin) * endFraction;
        const visibleRange = visibleEnd - visibleStart;
        const oneDay = 24 * 60 * 60 * 1000; // 24時間をミリ秒で

        const dataItem = target.dataItem as any;
        if (dataItem) {
          const value = dataItem.get("value");
          if (value) {
            const date = new Date(value);

            if (visibleRange > oneDay) {
              // ズームアウト状態の場合：日付が変わるタイミングのみ MM/dd を表示
              const idx = XAxis.dataItems.indexOf(dataItem);
              // 先頭のラベルは常に表示
              if (idx === 0) {
                return root.dateFormatter.format(date, "MM/dd");
              }
              const prevDataItem = XAxis.dataItems[idx - 1];
              if (prevDataItem) {
                const prevValue = prevDataItem.get("value");
                if (prevValue) {
                  const prevDate = new Date(prevValue);
                  // 年/月/日 が異なる場合、日付が変わったと判断
                  if (
                    date.getFullYear() !== prevDate.getFullYear() ||
                    date.getMonth() !== prevDate.getMonth() ||
                    date.getDate() !== prevDate.getDate()
                  ) {
                    return root.dateFormatter.format(date, "MM/dd");
                  }
                }
              }
              // 日付が変わっていない場合は何も表示しない
              return "";
            } else {
              // ズームイン状態の場合：全て HH:mm 形式で表示
              return root.dateFormatter.format(date, "HH:mm");
            }
          }
        }
        return text;
      }
    );

    // シリーズ作成
    const series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        xAxis: XAxis, // 時間軸を基準にする
        yAxis,
        categoryYField: "yDisp",
        openValueXField: "startDate",
        valueXField: "endDate",
        clustered: false,
      })
    );

    yAxis.get("renderer").labels.template.setAll({
      text: "{yDisp}",
      centerY: am5.p50,
      dy: 0,
      fill: labelColor,
    });

    // エリア単位
    if (reportForm.unit === "2") {
      // 色を順番に割り当てる
      legend?.forEach((color) => {
        areaColors[color.areaName] = am5.color(color.areaColor);
      });
      // 色を area に基づいて変更
      series.columns.template.adapters.add("fill", (_, target) => {
        const dataItem = target.dataItem?.dataContext as { area: string };
        const area = dataItem?.area || "default";
        return areaColors[area] || areaColors.default;
      });

      series.columns.template.adapters.add("stroke", (_, target) => {
        const dataItem = target.dataItem?.dataContext as { area: string };
        const area = dataItem?.area || "default";
        return areaColors[area] || areaColors.default;
      });

      setFilteredLegend(
        legend.filter((color) =>
          workerGroupedData.some((worker) =>
            worker.tasks.some((task) => task.area === color.areaName)
          )
        )
      );
    } else {
      // 色を順番に割り当てる
      legend?.forEach((color) => {
        const key = `${color.areaId}${color.areaName}`;
        areaColors[key] = am5.color(color.areaColor);
      });
      // 色を area に基づいて変更
      series.columns.template.adapters.add("fill", (_, target) => {
        const dataItem = target.dataItem?.dataContext as {
          areaId: string;
          area: string;
        };
        // areaIdとareaNameを連結してキーを生成
        const key = dataItem ? `${dataItem.areaId}${dataItem.area}` : "default";
        return areaColors[key] || areaColors.default;
      });

      series.columns.template.adapters.add("stroke", (_, target) => {
        const dataItem = target.dataItem?.dataContext as {
          areaId: string;
          area: string;
        };
        const key = dataItem ? `${dataItem.areaId}${dataItem.area}` : "default";
        return areaColors[key] || areaColors.default;
      });

      setFilteredLegend(
        legend.filter((color) =>
          workerGroupedData.some((worker) =>
            worker.tasks.some(
              (task) =>
                task.areaId === color.areaId && task.area === color.areaName
            )
          )
        )
      );
    }

    // 開始～終了のツールチップ
    series.columns.template.set(
      "tooltipText",
      `[bold]エリア：[/] {area}-{areaId}\n[bold]入時間：[/] {openValueX.formatDate('yyyy/MM/dd HH:mm')}\n[bold]退時間：[/] {valueX.formatDate('yyyy/MM/dd HH:mm')}`
    );

    series.columns.template.adapters.add("tooltipText", (text, target) => {
      const dataItem = target.dataItem;

      if (dataItem) {
        const context = dataItem.dataContext as any;
        if (context && context.is9999) {
          // 未退出に差し替える
          return (text ?? "").replace(
            /\{valueX\.formatDate\([^\}]+\)\}/,
            "未退出"
          );
        }
      }
      return text;
    });

    // X軸スクロールバー
    const scrollbarX = am5.Scrollbar.new(root, {
      orientation: "horizontal", // 水平方向のスクロールバー
    });
    chart.set("scrollbarX", scrollbarX);

    // Y軸
    const scrollbarY = am5.Scrollbar.new(root, {
      orientation: "vertical", // 縦方向のスクロールバー
    });
    chart.set("scrollbarY", scrollbarY);

    // Y軸の表示範囲を設定
    const totalCategories = workerGroupedData.length + 2;
    const visibleFraction = totalCategories > 6 ? 6 / totalCategories : 1;
    yAxis.set("start", 0);
    yAxis.set("end", visibleFraction);

    // X軸にデータを設定
    XAxis.data.setAll(workerGroupedData);

    // Y軸にデータを設定
    yAxis.data.setAll(workerGroupedData);

    // シリーズにデータを設定
    series.data.setAll(
      workerGroupedData.flatMap((worker) =>
        worker.tasks.map((task) => {
          const is9999 = task.endDate === "9999-12-31T00:00:00";

          return {
            yDisp: worker.yDisp,
            startDate: new Date(task.startDate).getTime(),
            endDate: is9999
              ? new Date().getTime()
              : new Date(task.endDate).getTime(),

            areaId: task.areaId,
            area: task.area,
            is9999: is9999,
          };
        })
      )
    );

    // アラートを描画
    const alertSeries = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        xAxis: XAxis,
        yAxis,
        categoryYField: "yDisp",
        openValueXField: "alertTime",
        valueXField: "alertTime",
        clustered: false,
      })
    );

    // 色やスタイルを設定
    alertSeries.columns.template.setAll({
      fill: am5.color("#FF0000"),
      stroke: am5.color("#FF0000"),
      width: 1,
    });

    alertSeries.columns.template.adapters.add(
      "width",
      (currentWidth, target) => {
        const desiredTimeSpan = 120000; // 3分 = 60000ms

        const absoluteMin = XAxis.get("min") as number;
        const absoluteMax = XAxis.get("max") as number;
        const startRel = XAxis.get("start") as number;
        const endRel = XAxis.get("end") as number;

        if (
          absoluteMin == null ||
          absoluteMax == null ||
          startRel == null ||
          endRel == null
        ) {
          return currentWidth;
        }

        const visibleMin = absoluteMin + (absoluteMax - absoluteMin) * startRel;
        const visibleMax = absoluteMin + (absoluteMax - absoluteMin) * endRel;
        const visibleTimeSpan = visibleMax - visibleMin;

        // X軸の現在の幅を取得
        const axisPixelWidth = XAxis.width(); // width() でピクセル幅を取得
        if (!axisPixelWidth || visibleTimeSpan === 0) {
          return currentWidth;
        }

        const newWidth = (desiredTimeSpan / visibleTimeSpan) * axisPixelWidth;

        return Math.max(newWidth, 1); // 幅が0以下にならないように調整
      }
    );

    // アラート時間のツールチップ
    alertSeries.columns.template.set(
      "tooltipText",
      `[bold]アラート時間：[/] {valueX.formatDate('yyyy/MM/dd HH:mm')}`
    );

    // 横方向のオフセット(dx)を付与するアダプターを設定
    alertSeries.columns.template.adapters.add("dx", (dx, target) => {
      const dataItem = target.dataItem;
      // 型アサーションを使って dataContext の offset プロパティにアクセス
      const context = dataItem?.dataContext as { offset?: number } | undefined;
      if (context && typeof context.offset === "number") {
        return context.offset;
      }
      return dx;
    });

    alertSeries.data.setAll(
      workerGroupedData.flatMap((worker) =>
        worker.tasks
          .filter((task) => task.alertTime)
          .map((task, index) => ({
            yDisp: worker.yDisp,
            alertTime: task.alertTime
              ? new Date(task.alertTime).getTime()
              : undefined,
            // offset: index * 0.5,
          }))
      )
    );

    // 強制退出を描画
    const forcedExitSeries = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        xAxis: XAxis,
        yAxis,
        categoryYField: "yDisp",
        openValueXField: "forcedExitTime",
        valueXField: "forcedExitTime",
        clustered: false,
      })
    );

    // 色やスタイルを設定
    forcedExitSeries.columns.template.setAll({
      fill: am5.color("#00CED1"),
      stroke: am5.color("#00CED1"),
      width: 3,
    });

    // 強制退出のツールチップ
    forcedExitSeries.columns.template.set(
      "tooltipText",
      `[bold]強制退出：[/] {valueX.formatDate('yyyy/MM/dd HH:mm')}`
    );

    forcedExitSeries.data.setAll(
      workerGroupedData.flatMap((worker) =>
        worker.tasks
          .filter((task) => task.forcedExitTime)
          .map((task, index) => ({
            yDisp: worker.yDisp,
            forcedExitTime: task.forcedExitTime
              ? new Date(task.forcedExitTime).getTime()
              : undefined,
            //offset: index * 3,
          }))
      )
    );

    // X軸のタイトル（時間）
    chart.plotContainer.children.push(
      am5.Label.new(root, {
        text: "日時",
        fontSize: 14,
        fontWeight: "bold",
        textAlign: "center",
        x: am5.p50, // X軸の中央
        y: am5.p100, // チャートの一番下に配置
        centerX: am5.p50,
        paddingTop: 30, // X軸との間隔
        fill: labelColor,
      })
    );

    // Y軸のタイトル
    chart.plotContainer.children.push(
      am5.Label.new(root, {
        text: "作業者名（タグID）", // 各文字を改行
        fontSize: 14,
        fontWeight: "bold",
        textAlign: "center",
        rotation: 0, // 縦書きにする
        y: -20, // チャートの中央に配置
        x: -100, // チャートの左端
        centerY: am5.p50,
        paddingRight: 10,
        fill: labelColor,
      })
    );

    return () => root.dispose();
  }, [workerGroupedData, reportForm, legend, mode, setFilteredLegend]);

  return (
    <>
      <ReportLegend reportForm={reportForm} legend={filteredLegend} />
      <Box
        ref={timeAxisChartRef}
        sx={{
          minWidth: "700px",
          width: "100%",
          height: {
            xs: "500px",
            sm: chartHeight - 40,
            md: chartHeight - 40,
            lg: chartHeight,
          },
          overflowY: "auto",
          display: "block",
        }}
      />
    </>
  );
};
